import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "immobili_portal_token";
const USER_KEY = "immobili_portal_user";

const ImmobiliPortalAuthContext = createContext(null);

function viewerMustChangePassword(viewer) {
  if (!viewer) return false;
  if (viewer.password_changed_at) return false;
  if (Object.prototype.hasOwnProperty.call(viewer, "must_change_password")) {
    return Boolean(viewer.must_change_password);
  }
  return true;
}

export function ImmobiliPortalAuthProvider({ children }) {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const persistUser = useCallback((viewer) => {
    setUser(viewer);
    if (viewer) {
      localStorage.setItem(USER_KEY, JSON.stringify(viewer));
    } else {
      localStorage.removeItem(USER_KEY);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return null;
    const { data } = await axios.get(`${API_URL}/api/immobili-portal/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    persistUser(data);
    return data;
  }, [token, persistUser]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    refreshUser()
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setToken(null);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [token, refreshUser]);

  const login = useCallback(
    async (email, password) => {
      const { data } = await axios.post(`${API_URL}/api/immobili-portal/auth/login`, {
        email,
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      persistUser(data.viewer);
      setToken(data.token);
      toast.success("Accesso effettuato");
      navigate("/portale-immobiliare");
    },
    [navigate, persistUser]
  );

  const changePassword = useCallback(
    async (newPassword, confirmPassword) => {
      const { data } = await axios.post(
        `${API_URL}/api/immobili-portal/auth/change-password`,
        { new_password: newPassword, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.viewer) {
        persistUser(data.viewer);
      } else {
        await refreshUser();
      }
      toast.success("Password aggiornata");
    },
    [token, refreshUser, persistUser]
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
    navigate("/portale-immobiliare/login");
  }, [navigate]);

  const mustChangePassword = viewerMustChangePassword(user);

  return (
    <ImmobiliPortalAuthContext.Provider
      value={{
        token,
        user,
        loading,
        isAuthenticated: Boolean(token && user),
        mustChangePassword,
        login,
        logout,
        changePassword,
        refreshUser,
      }}
    >
      {children}
    </ImmobiliPortalAuthContext.Provider>
  );
}

export function useImmobiliPortalAuth() {
  const ctx = useContext(ImmobiliPortalAuthContext);
  if (!ctx) throw new Error("useImmobiliPortalAuth must be used within ImmobiliPortalAuthProvider");
  return ctx;
}
